'use client';

import useInfiniteScroll from 'react-infinite-scroll-hook';

import { type CurrencySymbols } from '@/features/currency/types/currency-types';
import ListingDetailed from '@/features/listing/components/listing-detailed/listing-detailed';
import useLoadListings from '@/features/listing/hooks/use-load-listings';
import { type ListingType } from '@/features/listing/types/listing-types';
import { type Place } from '@/features/place/types/place-types';
import Divider from '@/ui/divider/divider';
import Loader from '@/ui/loader/loader';

import styles from './listing-infinity-loader.module.scss';

interface ListingsInfinityScrollProps {
  listingType: ListingType;
  placeBbox: Place['bbox'];
  currencyName: CurrencySymbols['name'];
  wrapperClassName?: string;
}

export default function ListingsInfinityScroll(props: ListingsInfinityScrollProps) {
  const {
    currencyName,
    listingType,
    placeBbox,
    wrapperClassName,
  } = props;

  const {
    error,
    hasNextPage,
    listings,
    loadMoreListings,
    loading,
  } = useLoadListings(listingType, placeBbox, currencyName);

  const [sentryRef] = useInfiniteScroll({
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: !!error,
    hasNextPage,
    loading,
    onLoadMore: loadMoreListings,
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 400px 0px',
  });

  return (
    <>
      {listings.map((listing) => (
        <div key={listing.id}>
          <div className={wrapperClassName}>
            <ListingDetailed listing={listing} />
          </div>
          <Divider />
        </div>
      ))}

      {(loading || hasNextPage) && (
        <div className={styles.listingsInfinityScroll_loader} ref={sentryRef}>
          <Loader />
        </div>
      )}
    </>
  );
}
